import React, { Component, Fragment } from 'react';

import { Grid } from 'components/grid';
import { Select } from 'components/input';
import { ACTIONS } from 'components/input/constants';
import { translate } from 'components/translate';
import ClubDate from 'helpers/ClubDate';
import { createSelectOptions } from 'helpers/Object';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';

import AccessSelectDateFooter from './AccessSelectDateFooter';
import AccessSelectDateHeader from './AccessSelectDateHeader';
import AccessSelectDatePickers from './AccessSelectDatePickers';

import './AccessSelectDate.scss';

const days = {
  thirty: 30,
  sixty: 60,
  ninety: 90,
  hundredEigthy: 180,
  threeHundredSixtyFive: 365
};

const EMPTY_STRING = '';

const RANGE_VALUE = 'RANGE';

const initialState = {
  value: '',
  beginDate: '',
  endDate: ''
};

@translate()
@inject('UserStore')
@observer
export default class AccessSelectDate extends Component {
  static propTypes = {
    field: PropTypes.string,
    fieldData: PropTypes.object,
    cleanData: PropTypes.func,
    onMenuOpen: PropTypes.func,
    setValue: PropTypes.func,
    t: PropTypes.func,
    UserStore: PropTypes.object,
    setClearFunction: PropTypes.func
  };

  static defaultProps = {
    fieldData: {}
  };

  state = {
    ...initialState,
    periodList: []
  };

  options = [];

  constructor(props) {
    super(props);

    this.onToogleOpen = this.onToogleOpen.bind(this);
  }

  componentDidMount() {
    const { setClearFunction } = this.props;

    this.setupPeriods();
    setClearFunction && setClearFunction(this.clearState);
  }

  onToogleOpen() {
    const { field, onMenuOpen } = this.props;

    onMenuOpen && onMenuOpen(field);
  }

  setupPeriods() {
    const { fieldData, t } = this.props;
    const currentValue = fieldData.value || {};
    const values = [
      'LAST_30',
      'LAST_60',
      'LAST_90',
      'LAST_180',
      'LAST_365',
      RANGE_VALUE
    ];
    const labels = [
      this.labelDays(days.thirty),
      this.labelDays(days.sixty),
      this.labelDays(days.ninety),
      this.labelDays(days.hundredEigthy),
      this.labelDays(days.threeHundredSixtyFive),
      t('users_manager.deep_search.access.period_options.choose_the_period')
    ];

    const periodsAsList = createSelectOptions(values, labels).map((period) => {
      return { ...period, checked: period.value === currentValue.value };
    });

    this.beginDate = ClubDate.resolve(currentValue.from);
    this.endDate = ClubDate.resolve(currentValue.until);

    this.setState({
      periodList: periodsAsList,
      value: currentValue.value,
      beginDate:
        (this.beginDate && this.beginDate.at.startOfDay().to.timestamp()) ||
        EMPTY_STRING,
      endDate:
        (this.endDate && this.endDate.at.endOfDay().to.timestamp()) ||
        EMPTY_STRING
    });
  }

  clearState = () => {
    const { periodList } = this.state;
    const resetPeriodList = periodList.map((period) => ({
      ...period,
      checked: false
    }));

    this.beginDate = null;
    this.endDate = null;

    this.setState({ ...initialState, periodList: resetPeriodList });
  };

  labelDays(days) {
    return this.props.t(
      'users_manager.deep_search.access.period_options.last_days',
      { data: { days } }
    );
  }

  get label() {
    if (this.isDateRange()) {
      return this.formattedDateRange;
    }

    return this.state.periodList.find(
      (period) => period.value === this.state.value
    ).label;
  }

  get formattedDateRange() {
    const { t } = this.props;
    const getFormattedDate = (date) => (date && date.format()) || EMPTY_STRING;

    if (!this.state.beginDate && !this.state.endDate) {
      return EMPTY_STRING;
    }

    const beginDateFormatted = getFormattedDate(this.beginDate);
    const endDateFormatted = getFormattedDate(this.endDate);

    let messageTemplate = '';

    if (this.state.beginDate && this.state.endDate) {
      messageTemplate = 'users_manager.deep_search.access.between_dates';
    } else if (this.state.beginDate) {
      messageTemplate = 'users_manager.deep_search.access.begin_date';
    } else {
      messageTemplate = 'users_manager.deep_search.access.end_date';
    }

    return t(messageTemplate, {
      data: {
        beginDate: beginDateFormatted,
        endDate: endDateFormatted
      }
    });
  }

  get value() {
    if (!this.state.value) {
      return EMPTY_STRING;
    }

    return {
      value: this.state.value,
      label: this.label,
      name: this.state.value
    };
  }

  isDateRange = (value) => {
    const valueToBeCompared = value || this.state.value;

    return valueToBeCompared === RANGE_VALUE;
  };

  setCheck = (event) => {
    const value = event.target.name;

    this.setCheckByValue(value);
  };

  setCheckByValue = (value) => {
    const { beginDate, endDate } = this.getDateIntervalByValue(value);

    this.setState((prevState) => ({
      periodList: prevState.periodList.map((period) => {
        period.checked = period.value === value;

        return period;
      }),
      value,
      beginDate,
      endDate
    }));
  };

  getDateIntervalByValue = (value) => {
    this.beginDate = null;
    this.endDate = null;

    let beginDate = EMPTY_STRING;
    let endDate = EMPTY_STRING;

    if (this.isDateRange(value)) {
      this.beginDate = ClubDate.resolve(this.state.beginDate);
      this.endDate = ClubDate.resolve(this.state.endDate);

      beginDate =
        (this.beginDate && this.beginDate.at.startOfDay().to.timestamp()) ||
        EMPTY_STRING;
      endDate =
        (this.endDate && this.endDate.at.endOfDay().to.timestamp()) ||
        EMPTY_STRING;
    }

    return {
      beginDate,
      endDate
    };
  };

  setValue = () => {
    const { field, setValue } = this.props;
    const { beginDate, endDate } = this.getDateIntervalByValue(
      this.state.value
    );

    // we should send dates endDateAsISOString to the search API
    const beginDateAsISOString =
      (beginDate && this.beginDate.to.ISOString()) || '';
    const endDateAsISOString = (endDate && this.endDate.to.ISOString()) || '';

    setValue(field, this.value, beginDateAsISOString, endDateAsISOString);
  };

  onDateChange = (timestamp, value) => {
    this.setState({ [value]: timestamp });
  };

  clearInput = (_value, { action }) => {
    if (action === ACTIONS.CLEAR) {
      this.clearField();
    }
  };

  clearField = () => {
    const { cleanData, field } = this.props;

    this.clearState();
    cleanData && field && cleanData(field);
  };

  selectedOption = (value) =>
    this.state.periodList.find((item) => item.value === value);

  render() {
    const { field, fieldData, cleanData } = this.props;
    const value = this.selectedOption(fieldData.value.value);

    return (
      <div className="access__select__container">
        <Select
          name="class"
          classNamePrefix="select"
          placeholder="users_manager.deep_search.access.choose_period"
          onToogleOpen={this.onToogleOpen}
          onChange={this.clearField}
          isSearchable={false}
          menuIsOpen={false}
          value={value}
          marginBottom={false}
          closeClickingOutside={false}
          isClearable
        />
        {fieldData.open && (
          <Fragment>
            <Grid className="access__select__grid__container access__select__grid__dates">
              <AccessSelectDateHeader
                field={field}
                setCheck={this.setCheck}
                periodList={this.state.periodList}
              />
              <AccessSelectDatePickers
                onDateChange={this.onDateChange}
                isDateDisabled={!this.isDateRange()}
                beginDate={this.state.beginDate}
                endDate={this.state.endDate}
              />
            </Grid>

            <AccessSelectDateFooter
              cleanData={cleanData}
              field={field}
              uncheckAllOptions={this.clearState}
              setValue={this.setValue}
            />
          </Fragment>
        )}
      </div>
    );
  }
}
